import { ThemeContext } from 'styled-components'
import React, { useContext, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form, Tab } from 'semantic-ui-react'
import ColorPicker from '~/components/ColorPicker'
import { FormFields } from '~/components/FormFields'
import { El } from '~/components/Layout/style'
import { StyledMenuItem, StyledTabPane } from '../../General/style'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import Perimeters from '../../Co2/Tabs/General/Perimeters'

interface Props {
  startingColVal: {
    yourValueColor: string
    lflValueColor: string
  }
}

const General: React.FC<Props> = ({ startingColVal }) => {
  const { t } = useTranslation()
  const { control, setValue, getValues, reset, formState } = useFormContext()
  const { firstColor, secondaryColor } = useContext(ThemeContext)
  const data = [
    { name: t(''), uv: 4000 },
    { name: t(''), pv: 1398 }
  ];

  const watchPrimaryColor = useWatch({ control, name: 'yourValueColor' })
  const watchSecondaryColor = useWatch({ control, name: 'lflValueColor' })

  const [activeTabIndex, setActiveTabIndex] = useState<string | number>(0)

  function getMenuItem(label, index) {
    const languagesErrors = formState.errors?.texts ? formState?.errors?.texts[0]?.languages : {}
    const paneHasError = index in (languagesErrors || {});
    return <StyledMenuItem
      key={label}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{label}</div>
    </StyledMenuItem>
  }

  function renderLanguageTabs() {
    const languages = getValues('texts[0].languages')
    return <Tab
      activeIndex={activeTabIndex}
      onTabChange={(_, data) => setActiveTabIndex(data.activeIndex)}
      renderActiveOnly={false}
      menu={{ secondary: true, pointing: true }}
      panes={languages?.map((language, index) => {
        return {
          menuItem: getMenuItem(language.label, index),
          key: language.id,
          pane: <StyledTabPane
            active={index === activeTabIndex}
            key={`${language.id}-${index}`}
          >
            <FormFields.Input
              control={control}
              name={`texts[0].languages[${index}].translation`}
              label={t('Average THG Emissions Chart Text')}
              placeholder={t('Average THG Emissions Chart Text')}
              rules={{
                required: {
                  value: true,
                  message: t('Please enter Average GHG Emissions Chart Text')
                }
              }}
            />
          </StyledTabPane>
        }
      })}
    />
  }

  function renderChartColorExample() {
    return <ResponsiveContainer width='100%' height={250}>
      <BarChart
        layout='vertical' // Set the layout to vertical to align bars along the y-axis
        data={data}
        margin={{ top: 20, right: 30, left: -60, bottom: 5 }}
        maxBarSize={30}
      >
        <CartesianGrid strokeDasharray='3 3' />
        <XAxis type='number' />
        <YAxis type='category' dataKey='name' />
        <Legend />
        <Bar dataKey='uv' stackId='a' fill={watchPrimaryColor} />
        <Bar dataKey='pv' stackId='a' fill={watchSecondaryColor} />
      </BarChart>
    </ResponsiveContainer>
  }

  return <div style={{ padding: 20 }}>
    <FormFields.Checkbox
      control={control}
      name='useMilkPrice'
      defaultValue={0}
      checkboxValue={1}
      label={t('Display milk price')}
    />
    <FormFields.Checkbox
      control={control}
      name='applyForageProductionInDataCollection'
      defaultValue={0}
      checkboxValue={1}
      label={t('Apply forage production in the context of data collection?')}
    />
    <FormFields.Checkbox
      control={control}
      name='showLflDefaultValue'
      defaultValue={0}
      checkboxValue={1}
      label={t('Show LfL standard values ​​in the dashboard/reporting')}
    />
    <div style={{ display: 'flex', alignItems: 'baseline', flexDirection: 'column' }}>
      <Form.Group style={{ alignItems: 'center' }}>
        <FormFields.External
          fluid
          name={'yourValueColor'}
          label={t('Primary color')}
          control={control}
          rules={{
            required: {
              value: true,
              message: t('Please enter color')
            }
          }}
        >
          <ColorPicker value={watchPrimaryColor || ''}>
            <FormFields.Input
              control={control}
              hideError
              onChange={(e, data) => setValue('yourValueColor', data.value)}
              placeholder={t('Primary color')}
              name={'yourValueColor'}
            />
          </ColorPicker>
        </FormFields.External>
        <FormFields.External
          fluid
          name={'lflValueColor'}
          label={t('Secondary color')}
          control={control}
          rules={{
            required: {
              value: true,
              message: t('Please enter color')
            }
          }}
        >
          <ColorPicker value={watchSecondaryColor || ''}>
            <FormFields.Input
              control={control}
              hideError
              onChange={(e, data) => setValue('lflValueColor', data.value)}
              placeholder={t('Secondary color')}
              name={'lflValueColor'}
            />
          </ColorPicker>
        </FormFields.External>
        <El.PageHeaderActionButton
          style={{ marginTop: 22 }}
          type='button'
          onClick={() => {
            reset({
              ...getValues(),
              yourValueColor: startingColVal.yourValueColor,
              lflValueColor: startingColVal.lflValueColor
            })
          }}>
          {t('Reset')}
        </El.PageHeaderActionButton>
        <El.PageHeaderActionButton
          style={{ marginTop: 22 }}
          type='button'
          onClick={() => {
            reset({
              ...getValues(),
              yourValueColor: firstColor,
              lflValueColor: secondaryColor || firstColor
            })
          }}>
          {t('Theme colors')}
        </El.PageHeaderActionButton>
      </Form.Group>
    </div>
    {renderChartColorExample()}
    {renderLanguageTabs()}
    <Perimeters
      control={control}
    />
  </div>
}
export default General